import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import MillieApi from 'app/apis/millie';
import Icon from 'app/components/common/icon';
import { NONPROFIT_DEFAULT_IMG_URL } from 'app/constants';
import utils from 'app/helpers/utils';
import history from 'app/history';
import paths from 'app/paths';

const searchNonprofits = (searchStr) => {
  let cancelled = false;
  const cancel = () => {cancelled = true;};
  const promise = new Promise((resolve, reject) => {
    MillieApi.nonprofitsSearch({search: searchStr, forceIncludeUnapproved: true}).then((response) => {
      if (cancelled) return reject(new Error('cancelled'));
      resolve(response.nonprofits.map((np) => ({
        id: np.id,
        imgUrl: np.imgUrl,
        name: np.name,
        ein: np.ein,
        location: (np.city && np.state) ? `${np.city}, ${np.state}` : null,
      })));
    }).catch(e => reject(e));
  });
  return [promise, cancel];
};

// It's hard to tell exactly what this component does. Comments would help here,
// especially a high-level description of capabilities. [jk:document]
//
// At one time this component was included in the Donate (i.e. 'nonprofits')
// page. Now it appears to be used only by company admins, so its results
// disregard the `searchApprovedNonprofitsOnly` flag of the user's company.
class SearchField extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      inputVal: '',
      currentSearchStr: null,
      searching: false,
      nonprofits: null,
      selectedIndex: 0,
    };

    this.rejectSearchProm = () => {};

    this.refInput = React.createRef();
    this.refMenu = React.createRef();

    this.search = this.search.bind(this);
    this.autoComplete = _.debounce(this.autoComplete.bind(this), 500);
    this.onKeyUpInput = this.onKeyUpInput.bind(this);
    this.onChangeInput = this.onChangeInput.bind(this);
    this.onFocusInput = this.onFocusInput.bind(this);
    this.onBlurInput = this.onBlurInput.bind(this);
    this.onClickSearch = this.onClickSearch.bind(this);
  }

  componentWillUnmount() {
    this.rejectSearchProm();
    this.autoComplete.cancel();
  }

  /** Clears the search text. */
  reset() {
    this.setState({inputVal: ''});
  }

  /*
   *  Tasks
   */

  submit() {
    const { onSelect } = this.props;
    const { selectedIndex, nonprofits } = this.state;
    this.refInput.current && this.refInput.current.blur();
    if (!selectedIndex && !onSelect) {
      return this.search();
    }
    const nonprofit = nonprofits[selectedIndex - 1];
    if (nonprofit) this.goToNonprofit(nonprofit);
  }

  scrollSelectedIntoView() {
    // doesn't work well in safari
    if (utils.getBrowser() === 'Safari') return;
    process.nextTick(() => {
      const selectedElement = document.querySelector('.search-field-menu-np.selected');
      if (!selectedElement) return;
      selectedElement.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    });
  }

  decrementSelectedIndex() {
    const selectedIndex = Math.max(0, (this.state.selectedIndex - 1));
    this.setState({selectedIndex});
    this.scrollSelectedIntoView();
  }

  incrementSelectedIndex() {
    const nonprofitCount = this.state.nonprofits ? this.state.nonprofits.length : 0;
    const selectedIndex = Math.min(this.state.selectedIndex + 1, nonprofitCount);
    this.setState({selectedIndex});
    this.scrollSelectedIntoView();
  }

  autoComplete(searchStr) {
    if (searchStr === this.state.currentSearchStr) return;
    if (!searchStr) {
      return this.setState({searching: false, nonprofits: null, currentSearchStr: searchStr, selectedIndex: 0});
    }
    this.setState({searching: true, nonprofits: null, currentSearchStr: searchStr, selectedIndex: 0});
    const [prom, reject] = searchNonprofits(searchStr);
    this.rejectSearchProm = reject;
    prom.then((nonprofits) => {
      this.onResults(searchStr, nonprofits);
    }).catch(() => {});
  }

  /** Checks the value of the input and, if it contains non-whitespace
   *  characters, merges that `search` value into the `pathParams` prop,
   *  constructs a new path that encodes the combined search parameters, and
   *  navigates to that path. */
  search() {
    const { pathParams } = this.props;
    const search = (this.refInput.current.value || '').trim();
    if (!search) return;
    const path = paths.nonprofits({...(pathParams || {}), search});
    // This seems to be left over from the days when this component was used in
    // the Donate page. Tyler says it can be removed, and suggests that it be
    // moved to the 'app/components/company-admin/' folder: [jk:refactor]
    history.push(path);
  }

  goToNonprofit(nonprofit) {
    const { onSelect } = this.props;
    if (onSelect) {
      onSelect(nonprofit);
    } else {
      const path = paths.nonprofit(nonprofit);
      history.push(path);
    }
  }

  /*
   *  Events
   */

  onChangeInput() {
    this.setState({inputVal: this.refInput.current.value});
  }

  onKeyUpInput(event) {
    if (event.key === 'Enter') return this.submit();
    if (event.key === 'ArrowUp') return this.decrementSelectedIndex();
    if (event.key === 'ArrowDown') return this.incrementSelectedIndex();
    const searchStr = this.refInput.current.value.trim();
    this.autoComplete(searchStr);
  }

  onFocusInput() {
    this.setState({focused: true});
  }

  onBlurInput(event) {
    const { relatedTarget } = event;
    const clickedResult = relatedTarget && this.refMenu.current && this.refMenu.current.contains(relatedTarget);
    if (!clickedResult) {
      this.setState({focused: false});
    }
  }

  onResults(searchStr, nonprofits) {
    if (searchStr !== this.state.currentSearchStr) return;
    this.setState({nonprofits, searching: false});
  }

  onClickNonprofit(nonprofit) {
    this.setState({focused: false});
    this.goToNonprofit(nonprofit);
  }

  onClickSearch() {
    this.setState({focused: false});
    this.search();
  }

  /*
   *  Render
   */

  render() {
    const { focused, nonprofits, inputVal, selectedIndex, searching } = this.state;
    const { className, small, onSelect, placeholder } = this.props;
    const focusedClass = focused ? 'focused' : 'blurred';
    const smallClass = small ? 'small' : '';
    const showMenu = !!(focused && inputVal.trim());
    const IconComp = (onSelect && searching) ? Icon.Loading : Icon.Search;

    return (
      <div className={`search-field ${className} ${focusedClass} ${smallClass}`}>
        <div className="search-field-input-con">
          <input
            type="text"
            className="search-field-input"
            placeholder={placeholder}
            onKeyUp={this.onKeyUpInput}
            ref={this.refInput}
            onFocus={this.onFocusInput}
            onBlur={this.onBlurInput}
            onChange={this.onChangeInput}
            value={inputVal}
          />
          <IconComp className="search-field-glass" />
        </div>
        {showMenu &&
          <div className="search-field-menu" ref={this.refMenu}>
            {!onSelect && (
              <div onClick={this.onClickSearch} tabIndex="0" className={`search-field-menu-np ${selectedIndex ? '' : 'selected'}`}>
                <Icon.Search className="search-field-menu-np-glass" />
                <div className="search-field-menu-np-text">
                  <div className="search-field-menu-np-name">
                    Search: &quot;{inputVal.trim()}&quot;
                  </div>
                </div>
              </div>
            )}
            {!!(nonprofits && nonprofits.length) && (
              nonprofits.map((nonprofit, i) => (
                <div onClick={this.onClickNonprofit.bind(this, nonprofit)} tabIndex="0" className={`search-field-menu-np ${((selectedIndex - 1) === i) ? 'selected' : ''}`} key={nonprofit.id}>
                  <img src={nonprofit.imgUrl || NONPROFIT_DEFAULT_IMG_URL} alt="nonprofit thumbnail" className="search-field-menu-np-image" />
                  <div className="search-field-menu-np-text">
                    <div className="search-field-menu-np-name">{nonprofit.name}</div>
                    <div className="search-field-menu-np-subtext">
                      <div className="search-field-menu-np-ein">{nonprofit.ein}</div>
                      <div className="search-field-menu-np-location">{nonprofit.location}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        }
      </div>
    );
  }

}

SearchField.propTypes = {
  pathParams: PropTypes.object,
  className: PropTypes.string.isRequired,
  small: PropTypes.bool.isRequired,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
};

SearchField.defaultProps = {
  className: '',
  small: false,
  placeholder: 'Search nonprofits',
};

export default SearchField;
